import type { Map as MapLibreMap } from "maplibre-gl";

import { getMapInstance } from "../store/ephemeral/map/mapInstanceRegistry";
import { estimateJsonUtf8Bytes } from "./MemoryEstimate.tools";

/** One GeoJSON source ranked by serialized payload size. */
export interface MapLibreGeojsonSourceMetric {
    "sourceId":       string;
    "estimatedBytes": number;
}

/**
 * Best-effort MapLibre metrics visible from the public API.
 *
 * @remarks Tile caches (vector/raster) are usually the largest heap consumers but are
 * not exposed as serializable objects; `styleSerializedBytes` is only a lower bound.
 */
export interface MapLibreMemoryHints {
    "mapRegistered":                 boolean;
    "sourceCount":                   number;
    "layerCount":                    number;
    "imageCount":                    number;
    "geojsonSourceCount":            number;
    "vectorOrRasterSourceCount":     number;
    "sourceCountByType":             Record<string, number>;
    "topGeojsonSources":             MapLibreGeojsonSourceMetric[];
    "styleSerializedBytes":          number;
    "geojsonPayloadSerializedBytes": number;

    /** styleSerializedBytes + geojsonPayloadSerializedBytes (included in tracked totals). */
    "mapLibreTrackedBytes": number;
    "note":                 string;
}

/** Placeholder hints when the map is absent or the style is not loaded yet. */
function buildUnavailableMapLibreHints( note: string, mapRegistered: boolean ): MapLibreMemoryHints {
    return {
        mapRegistered,
        "sourceCount":                   0,
        "layerCount":                    0,
        "imageCount":                    0,
        "geojsonSourceCount":            0,
        "vectorOrRasterSourceCount":     0,
        "sourceCountByType":             {},
        "topGeojsonSources":             [],
        "styleSerializedBytes":          0,
        "geojsonPayloadSerializedBytes": 0,
        "mapLibreTrackedBytes":          0,
        note,
    };
}

/**
 * Estimates serialized size of GeoJSON attached to style sources.
 *
 * @param map Active MapLibre map.
 * @param includePayloadBytes When false, returns 0 without stringify (standard scan).
 * @returns Byte sum for `geojson` sources only.
 */
function measureGeojsonPayloadBytes( map: MapLibreMap, includePayloadBytes: boolean ): {
    "totalBytes": number;
    "topSources": MapLibreGeojsonSourceMetric[];
} {
    if ( !includePayloadBytes ) {
        return {
            "totalBytes": 0,
            "topSources": [],
        };
    }

    const style = map.getStyle();
    if ( style === undefined ) {
        return {
            "totalBytes": 0,
            "topSources": [],
        };
    }

    const sources = style.sources ?? {};
    const ranked: MapLibreGeojsonSourceMetric[] = [];

    for ( const [ sourceId, source ] of Object.entries( sources ) ) {
        if ( source === undefined || typeof source !== "object" ) {
            continue;
        }

        const typed = source as {
            "type"?: string;
            "data"?: unknown;
        };
        if ( typed.type === "geojson" ) {
            ranked.push( {
                "sourceId":       sourceId,
                "estimatedBytes": estimateJsonUtf8Bytes( typed.data ?? source ),
            } );
        }
    }

    ranked.sort( ( left, right ) => right.estimatedBytes - left.estimatedBytes );

    return {
        "totalBytes": ranked.reduce( ( total, entry ) => total + entry.estimatedBytes, 0 ),
        "topSources": ranked.slice( 0, 3 ),
    };
}

/**
 * Collects MapLibre structure hints to explain heap not covered by FlowRide trackers.
 *
 * @param params Optional scan depth flags.
 * @returns Metrics when a map is registered, otherwise a placeholder row.
 */
export function measureMapLibreMemoryHints( params: { "includeSerialization"?: boolean } = {} ): MapLibreMemoryHints {
    const includeSerialization = params.includeSerialization ?? true;
    const map = getMapInstance();
    if ( map === undefined ) {
        return buildUnavailableMapLibreHints( "Map not mounted", false );
    }

    const style = map.getStyle();
    if ( style === undefined ) {
        return buildUnavailableMapLibreHints( "Map style not loaded yet", true );
    }

    const sources = style.sources ?? {};
    const sourceValues = Object.values( sources );

    const sourceCountByType: Record<string, number> = {};
    let geojsonSourceCount = 0;
    let vectorOrRasterSourceCount = 0;
    for ( const source of sourceValues ) {
        if ( source === undefined || typeof source !== "object" ) {
            continue;
        }

        const typed = source as { "type"?: string };
        const type = typed.type ?? "unknown";
        sourceCountByType[type] = ( sourceCountByType[type] ?? 0 ) + 1;

        if ( typed.type === "geojson" ) {
            geojsonSourceCount += 1;
        } else if ( typed.type === "vector" || typed.type === "raster" || typed.type === "raster-dem" ) {
            vectorOrRasterSourceCount += 1;
        }
    }

    const geojsonMetrics = measureGeojsonPayloadBytes( map, includeSerialization );
    const styleSerializedBytes = includeSerialization ? estimateJsonUtf8Bytes( style ) : 0;
    const mapLibreTrackedBytes = styleSerializedBytes + geojsonMetrics.totalBytes;

    return {
        "mapRegistered":                 true,
        "sourceCount":                   sourceValues.length,
        "layerCount":                    ( style.layers ?? [] ).length,
        "imageCount":                    map.listImages().length,
        geojsonSourceCount,
        vectorOrRasterSourceCount,
        sourceCountByType,
        "topGeojsonSources":             geojsonMetrics.topSources,
        styleSerializedBytes,
        "geojsonPayloadSerializedBytes": geojsonMetrics.totalBytes,
        mapLibreTrackedBytes,
        "note":                          vectorOrRasterSourceCount > 0
            ? "Vector/raster tile caches are NOT measured; they often explain UNACCOUNTED after pan/zoom."
            : "Style snapshot only; internal tile/GPU caches are opaque.",
    };
}
